import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { Button } from '@/shared/components/ui';
import { extractApiError } from '@/shared/lib/extractApiError';
import { formatBookingClock } from '@/shared/utils/bookingWhen';
import type { TripExtension } from '@/types';
import { tripExtensionApi, tripExtensionKeys } from '../api/tripExtension.api';
import { useExtensionOptions } from '../hooks/useTripExtensions';
import { formatExtensionMoney, formatExtraHours } from '../utils/extensionDisplay';

interface ExtraTimeDialogProps {
  bookingId: string;
  open: boolean;
  /** Traveler requests extra time; a guide suggests it. */
  viewer: 'traveler' | 'guide';
  onClose: () => void;
  onCreated?: (extension: TripExtension) => void;
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={strong ? 700 : 600} textAlign="right">
        {value}
      </Typography>
    </Stack>
  );
}

export function ExtraTimeDialog({ bookingId, open, viewer, onClose, onCreated }: ExtraTimeDialogProps) {
  const queryClient = useQueryClient();
  const optionsQuery = useExtensionOptions(bookingId, open);
  const data = optionsQuery.data;
  const [hours, setHours] = useState<number | null>(null);

  useEffect(() => {
    if (!open) setHours(null);
  }, [open]);

  useEffect(() => {
    // Preselect the shortest option; it is always the one most likely to still fit.
    if (open && hours == null && data?.options.length) setHours(data.options[0].hours);
  }, [open, hours, data]);

  const createMutation = useMutation({
    mutationFn: (selected: number) => tripExtensionApi.create(bookingId, selected),
    onSuccess: (extension) => {
      void queryClient.invalidateQueries({ queryKey: tripExtensionKeys.all });
      onCreated?.(extension);
      onClose();
    },
    onError: () => {
      // Someone else may have taken the time meanwhile; show the fresh options.
      void optionsQuery.refetch();
    },
  });

  const selected = data?.options.find((option) => option.hours === hours);
  const isGuide = viewer === 'guide';
  const error = createMutation.isError ? extractApiError(createMutation.error).message : null;

  return (
    <Dialog open={open} onClose={createMutation.isPending ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{isGuide ? 'Suggest extra time' : 'Add extra time'}</DialogTitle>
      <DialogContent>
        {optionsQuery.isLoading && (
          <CircularProgress size={28} sx={{ display: 'block', mx: 'auto', my: 3 }} />
        )}
        {optionsQuery.isError && (
          <Alert severity="error">{extractApiError(optionsQuery.error).message}</Alert>
        )}

        {data && (
          <Stack spacing={2}>
            {data.currentEndAt && (
              <Typography variant="body2" color="text.secondary">
                The trip currently ends at <strong>{formatBookingClock(data.currentEndAt)}</strong>.
                Extra time starts right after, in whole hours.
              </Typography>
            )}

            {data.openExtension ? (
              <Alert severity="info">
                There is already an extra-time request for this trip. Finish or withdraw it first.
              </Alert>
            ) : !data.eligible ? (
              <Alert severity="warning">{data.reason ?? 'Extra time is not available for this trip.'}</Alert>
            ) : (
              <>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    How much extra time?
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    value={hours}
                    onChange={(_, value: number | null) => value != null && setHours(value)}
                    sx={{ flexWrap: 'wrap', gap: 1, '& .MuiToggleButtonGroup-grouped': { border: 1, borderColor: 'divider', borderRadius: '8px !important', m: 0 } }}
                  >
                    {data.options.map((option) => (
                      <ToggleButton
                        key={option.hours}
                        value={option.hours}
                        sx={{ flexDirection: 'column', px: 2, py: 1, textTransform: 'none', minWidth: 96 }}
                      >
                        <Typography variant="body2" fontWeight={700}>
                          +{option.hours} h
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          until {formatBookingClock(option.newEndAt)}
                        </Typography>
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>

                {selected && data.hourlyRate != null && (
                  <Box>
                    <Divider sx={{ mb: 1 }} />
                    <Row label="New end time" value={formatBookingClock(selected.newEndAt)} />
                    <Row
                      label="Hourly rate"
                      value={`${formatExtensionMoney(data.hourlyRate, data.currency)}${
                        data.travelerCount > 1 ? ` × ${data.travelerCount} travelers` : ''
                      }`}
                    />
                    <Row label="Extra time" value={formatExtraHours(selected.hours)} />
                    <Row
                      label={isGuide ? 'Traveler pays' : 'Total to pay'}
                      value={formatExtensionMoney(selected.totalAmount, data.currency)}
                      strong
                    />
                    {isGuide && selected.estimatedGuideAmount != null && (
                      <Row
                        label="You earn (approx.)"
                        value={formatExtensionMoney(selected.estimatedGuideAmount, data.currency)}
                      />
                    )}
                  </Box>
                )}

                <Alert severity="info" variant="outlined">
                  {isGuide
                    ? 'The traveler has 15 minutes to approve and pay. The time is held for you until then.'
                    : 'Your guide needs to accept, then you pay online. The time is held for 15 minutes.'}
                  {' '}Extra time is non-refundable once it starts.
                </Alert>
              </>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button intent="secondary-outlined" onClick={onClose} disabled={createMutation.isPending}>
          Close
        </Button>
        <Button
          intent="primary-dark"
          disabled={
            !selected || !data?.eligible || Boolean(data?.openExtension) || createMutation.isPending
          }
          onClick={() => selected && createMutation.mutate(selected.hours)}
        >
          {createMutation.isPending ? 'Sending…' : isGuide ? 'Send suggestion' : 'Send request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
